import Card from '@mui/material/Card';

import PageContainer from '../../../components/Redesing/page-container';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchChip } from '../../../ts/interface/components/table.interface';
import useUrlState from '../../../hooks/use-url-state';
import { ACTIVE, DEFAULT_ROWS_PER_PAGE, MOVATIC_SHOP } from '../../../constants';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import {
  MovaticFieldGroupSelect,
  MovaticFieldGroupSelectFilter,
} from '../../../components/Redesing/movatic-field-group';
import { hardwareColumnsV2 } from '../../../tableColumns/hardwareColumns';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import StoreIcon from '@mui/icons-material/Store';

import { Button, MenuItem, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Stack } from '@mui/system';
import {
  useHardwareRelayQuery,
  HardwareQueryFilter,
  buildHardwareSort,
  mapHardwareToIHardware,
} from '../../../hooks/hardware/query/V2/useHardwareRelay';
import {
  useHardwareTypeCounts,
  topHardwareTypeNames,
} from '../../../hooks/hardware/query/V2/useHardwareTypeCounts';
import useHardwareTypeOrganization from '../../../hooks/hardware/query/useHardwareTypeOrganization';
import { IHardware, IHardwareType } from '../../../ts/interface/hardware.interface';
import { history, toTitleCase } from '../../../utils';
import useQueryString from '../../../hooks/queryParams/useQueryString';
import useOrganization from '../../../hooks/use-organization';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import { useKeysetPagination } from '../../../hooks/use-keyset-pagination';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { updateHardware } from '../../../api/hardware';
import customAsyncToast from '../../../components/Redesing/custom-async-toast';
import { IStatePillsOption } from '../../../ts/interface/components/redesing/state-pills.interface';
import { ISystem } from '../../../ts/interface/system.interface';
import OrganizationSystemSelect from '../../../components/system/organization-system-select';
import MultiActionChip from '../../../components/Redesing/multi-action-chip';
import { getHardwareIconByName } from '../../../util/utilsR';
import {
  QUICK_TYPE_LIMIT,
  isYesNo,
  stateOptions,
  toStates,
  toTypeNames,
  yesNoOptions,
} from '../hardwareFilterState';

function HardwareOrganizationPage() {
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumDevice = useMediaQuery(theme.breakpoints.down('md'));
  const shortDevice = useMediaQuery(theme.breakpoints.down('xs'));
  const isMediumScreen = isMediumDevice || shortDevice;

  const organizationInfo = useOrganization();

  const { queryParams, deleteQueryParam } = useQueryString();

  // The org dashboard's "Available <units>" tile links here with a HARDCODED
  // ?type=bike (dashboard/organizationView/resume.tsx), so `type` is not
  // trustworthy in this view and is deliberately NOT seeded into the filters —
  // doing so empties the list for every organization whose fleet isn't bikes.
  // ?status=active is seeded, since it matches what the tile counted.
  const [filters, setFilters, isCompleted, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    status: (queryParams.get('status') ? [ACTIVE] : []) as number[],
    available: '' as string,
    online: '' as string,
    inRental: '' as string,
    types: [] as string[],
    selectedSystem: {} as ISystem,
    system: '',
    search: '',
    sortConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
  });

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const debouncedSearchTerm = useSearchDebounce(filters.search);

  const [exportData, setExportData] = useState<boolean>(false);

  const appliedStates = useMemo(() => toStates(filters.status), [filters.status]);
  const appliedTypes = useMemo(() => toTypeNames(filters.types), [filters.types]);

  // Pending filter selections, committed to `filters` on Apply. State is a
  // multi-select (as in the system view) because the applied value is a list — a
  // single-value control seeded from states[0] silently dropped the rest of a
  // restored multi-state filter on the next Apply.
  const [pendingStates, setPendingStates] = useState<number[]>(() =>
    toStates(filters.status)
  );
  const [pendingTypes, setPendingTypes] = useState<string[]>(() =>
    toTypeNames(filters.types)
  );
  const [pendingAvailable, setPendingAvailable] = useState<string>('');
  const [pendingOnline, setPendingOnline] = useState<string>('');
  const [pendingInRental, setPendingInRental] = useState<string>('');
  const [pendingSystem, setPendingSystem] = useState<ISystem>({} as ISystem);

  const { data: hardwareTypeData = [], isLoading: isLoadingHardwareTypes } =
    useHardwareTypeOrganization(organizationInfo?.id, {
      enabled: Boolean(organizationInfo?.id),
    });

  // Every hardware type in use across the organization's systems — the full list
  // offered by the Type filter in the Filters menu.
  const typeOptions = useMemo(
    () =>
      (hardwareTypeData as IHardwareType[]).map((item) => ({
        value: item.name,
        label: toTitleCase(item.name),
        labelPlural: toTitleCase(item.name_plural),
      })),
    [hardwareTypeData]
  );

  const typeNames = useMemo(() => typeOptions.map((item) => item.value), [typeOptions]);

  const labelForType = useCallback(
    (name: string) =>
      typeOptions.find((item) => item.value === name)?.label ?? toTitleCase(name),
    [typeOptions]
  );

  // Quick-access toggles show only the most used types. Counts follow the same
  // scope as the list: a selected system narrows both.
  const { data: typeCounts, isLoading: isLoadingTypeCounts } = useHardwareTypeCounts({
    typeNames,
    systemId: filters.system || undefined,
    organizationId: filters.system ? undefined : organizationInfo?.id,
    enabled: Boolean(filters.system || organizationInfo?.id) && typeNames.length > 0,
  });

  const quickTypeNames = useMemo(() => {
    const ranked = topHardwareTypeNames(typeCounts, QUICK_TYPE_LIMIT);
    if (ranked.length > 0) return ranked;
    // Counts unavailable: while they load show nothing rather than a list that
    // reshuffles, but if the request failed fall back to the plain type list so
    // the toggles don't disappear altogether.
    return isLoadingTypeCounts ? [] : typeNames.slice(0, QUICK_TYPE_LIMIT);
  }, [typeCounts, isLoadingTypeCounts, typeNames]);

  // On URL restore, seed the pending controls from the persisted applied values
  // so the Filters dialog reflects what is actually filtering.
  // The `?? ''` matters: `online` and `inRental` are new keys, so a URL shared
  // before they existed restores them as undefined, and handing undefined to a
  // MUI Select that first rendered with '' flips it to uncontrolled.
  useEffect(() => {
    if (!isCompleted) return;
    setPendingStates(appliedStates);
    setPendingTypes(appliedTypes);
    setPendingAvailable(filters.available ?? '');
    setPendingOnline(filters.online ?? '');
    setPendingInRental(filters.inRental ?? '');
    setPendingSystem(filters.selectedSystem ?? ({} as ISystem));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted]);

  // Build server-side GraphQL filter (organization-scoped).
  const hardwareFilter = useMemo((): HardwareQueryFilter | undefined => {
    if (!organizationInfo?.id) return undefined;
    const filter: HardwareQueryFilter = { organizationId: organizationInfo.id };

    if (appliedStates.length > 0) {
      filter.state = appliedStates;
    }
    if (appliedTypes.length > 0) {
      filter.hardwareTypeName = appliedTypes;
    }
    if (filters.system) {
      filter.systemId = filters.system;
    }
    if (isYesNo(filters.available)) {
      filter.isAvailable = filters.available === 'true';
    }
    if (isYesNo(filters.online)) {
      filter.isOnline = filters.online === 'true';
    }
    if (isYesNo(filters.inRental)) {
      filter.isInRental = filters.inRental === 'true';
    }
    if (debouncedSearchTerm) {
      filter.search = debouncedSearchTerm;
    }
    return filter;
  }, [
    organizationInfo?.id,
    appliedStates,
    appliedTypes,
    filters.system,
    filters.available,
    filters.online,
    filters.inRental,
    debouncedSearchTerm,
  ]);

  const sortConfig = useMemo(
    () => buildHardwareSort(filters.sortConfig?.key, filters.sortConfig?.direction),
    [filters.sortConfig?.key, filters.sortConfig?.direction]
  );

  const {
    data: relayData,
    count: totalHardwareCount,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading: isLoadingHardware,
    isRefetching: isRefetchingHardware,
    refetch: refetchHardware,
  } = useHardwareRelayQuery(hardwareFilter, {
    limit: filters.sizePerPage,
    after,
    sort: sortConfig,
    enabled: isCompleted && Boolean(organizationInfo?.id),
  });

  // Only the current page's rows are shown (server-side pagination).
  const hardwareItems = useMemo(() => {
    if (!relayData) return [];
    return relayData.map(mapHardwareToIHardware);
  }, [relayData]);

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relayData]);

  const hardwareColumns = useMemo(() => {
    return hardwareColumnsV2(
      '',
      'hardware',
      '',
      '',
      '',
      true,
      (row: IHardware, item: IStatePillsOption) => {
        // @ts-ignore
        customAsyncToast({
          promise: () =>
            updateHardware(
              {
                id: row.old_id,
                state: item.value,
              },
              {
                hardware_type: 'hardware',
              }
            ),
          loadingMessage: 'Updating...',
          successMessage: () => {
            return 'Hardware updated successfully!';
          },
          errorMessage: 'Failed to update hardware',
        }).then(() => refetchHardware());
      },
      isMobileDevice,
      isMediumScreen,
      true,
      true
    ) as IColumn[];
  }, [isMobileDevice, isMediumScreen, refetchHardware]);

  const toggleType = useCallback(
    (name: string, selected: boolean) => {
      resetPagination();
      const next = selected
        ? [...appliedTypes, name]
        : appliedTypes.filter((item) => item !== name);
      setPendingTypes(next);
      setFilters((prevState) => ({ ...prevState, types: next }));
    },
    [appliedTypes, setFilters, resetPagination]
  );

  const quickTypeToggles = useMemo(
    () =>
      quickTypeNames.map((name) => {
        const option = typeOptions.find((item) => item.value === name);
        const { Icon } = getHardwareIconByName(name);
        return {
          label: option?.labelPlural ?? toTitleCase(name),
          id: name,
          icon: <Icon />,
          // MultiActionChip treats "active" as the untoggled state, so an
          // already-selected type starts inactive.
          defaultActive: !appliedTypes.includes(name),
          onActive: (id: string) => toggleType(id, true),
          onInactive: (id: string) => toggleType(id, false),
        };
      }),
    [quickTypeNames, typeOptions, appliedTypes, toggleType]
  );

  // Every chip is derived from the applied filters rather than held in its own
  // state, so rebuilding one group can't silently drop another.
  const displayedChips = useMemo(() => {
    const result: SearchChip[] = [];
    appliedStates.forEach((value) => {
      result.push({
        label: 'State',
        field: 'status',
        value,
        displayValue: stateOptions.find((option) => option.value === value)?.label,
      });
    });
    appliedTypes.forEach((name) => {
      result.push({
        label: 'Type',
        field: 'types',
        value: name,
        displayValue: labelForType(name),
      });
    });
    if (filters.system) {
      result.push({
        label: 'System',
        field: 'system',
        value: filters.system,
        displayValue: filters.selectedSystem?.name,
      });
    }
    const yesNoChips: Array<[string, string, string]> = [
      ['Available', 'available', filters.available],
      ['Online', 'online', filters.online],
      ['In Rental', 'inRental', filters.inRental],
    ];
    yesNoChips.forEach(([label, field, value]) => {
      if (!isYesNo(value)) return;
      result.push({
        label,
        field,
        value,
        displayValue: yesNoOptions.find((option) => option.value === value)?.label,
      });
    });
    return result;
  }, [
    appliedStates,
    appliedTypes,
    filters.system,
    filters.selectedSystem?.name,
    filters.available,
    filters.online,
    filters.inRental,
    labelForType,
  ]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      // PaginationTable groups chips and passes {label, values, disabled} to this
      // handler (no `field`), so match on the chip label.
      const clearByLabel: Record<string, () => void> = {
        State: () => {
          if (queryParams.get('status')) deleteQueryParam('status');
          setPendingStates([]);
          setFilters((prevState) => ({ ...prevState, status: [] }));
        },
        Type: () => {
          if (queryParams.get('type')) deleteQueryParam('type');
          setPendingTypes([]);
          setFilters((prevState) => ({ ...prevState, types: [] }));
        },
        System: () => {
          setPendingSystem({} as ISystem);
          setFilters((prevState) => ({
            ...prevState,
            system: '',
            selectedSystem: {} as ISystem,
          }));
        },
        Available: () => {
          setPendingAvailable('');
          setFilters((prevState) => ({ ...prevState, available: '' }));
        },
        Online: () => {
          setPendingOnline('');
          setFilters((prevState) => ({ ...prevState, online: '' }));
        },
        'In Rental': () => {
          setPendingInRental('');
          setFilters((prevState) => ({ ...prevState, inRental: '' }));
        },
      };

      clearByLabel[deletedGroup.label]?.();
      resetPagination();
    },
    [resetPagination, setFilters, deleteQueryParam, queryParams]
  );

  return (
    <PageContainer
      documentationLink={'https://documentation.movatic.co/admin-site/modes'}
      spaceName={'Hardware'}
    >
      <Card>
        <PaginationTable
          dataId="hardware"
          showFilter
          chips={displayedChips}
          isLoading={!isCompleted || isRestoring || isLoadingHardware || isPaginating}
          isLoadingCache={isRefetchingHardware}
          handleChipDelete={handleChipDelete}
          buttons={[
            {
              startIcon: <StoreIcon />,
              label: 'Buy',
              onClick: () => window.open(MOVATIC_SHOP, '_blank'),
            },
            {
              label: 'Export',
              startIcon: <FileDownloadIcon />,
              onClick: () => setExportData(true),
            },
            {
              label: 'Import',
              startIcon: <FileUploadIcon />,
              onClick: () => history.push('/hardware/add_csv'),
            },
          ]}
          exportData={exportData}
          csvFileName={'Hardware.csv'}
          localSearch={false}
          onExport={() => setExportData(false)}
          padding={{ pt: 1, pl: 0, pr: 1 }}
          onApplyFilter={() => {
            // Applying is a straight copy of the pending controls; every chip is
            // derived from these values.
            if (queryParams.get('status') && pendingStates.length === 0) {
              deleteQueryParam('status');
            }
            if (queryParams.get('type') && pendingTypes.length === 0) {
              deleteQueryParam('type');
            }
            setFilters((prevState) => ({
              ...prevState,
              status: pendingStates,
              types: pendingTypes,
              available: pendingAvailable,
              online: pendingOnline,
              inRental: pendingInRental,
              system: pendingSystem?.gid || '',
              selectedSystem: pendingSystem ?? ({} as ISystem),
            }));
            resetPagination();
          }}
          hasMore={graphqlHasMore}
          totalItems={totalHardwareCount}
          noDataSlice
          spaceFilters={
            <>
              <MovaticFieldGroupSelectFilter
                label="State"
                value={pendingStates
                  .map((value) =>
                    stateOptions.find((option) => option.value === Number(value))
                  )
                  .filter(Boolean)}
                onChange={(event: React.SyntheticEvent, newValue: any[]) =>
                  setPendingStates(
                    newValue
                      .filter((option) =>
                        stateOptions.some((item) => item.value === option.value)
                      )
                      .map((option) => option.value)
                  )
                }
                options={stateOptions}
              />

              <MovaticFieldGroupSelectFilter
                label="Type"
                isLoading={isLoadingHardwareTypes}
                value={pendingTypes.map(
                  (name) =>
                    typeOptions.find((option) => option.value === name) ?? {
                      value: name,
                      label: labelForType(name),
                    }
                )}
                onChange={(event: React.SyntheticEvent, newValue: any[]) =>
                  setPendingTypes(newValue.map((option) => option.value))
                }
                options={typeOptions}
              />

              <MovaticFieldGroupSelect
                label="Available"
                value={pendingAvailable}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setPendingAvailable(event.target.value)
                }
                optionNodes={yesNoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              />

              <MovaticFieldGroupSelect
                label="Online"
                value={pendingOnline}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setPendingOnline(event.target.value)
                }
                optionNodes={yesNoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              />

              <MovaticFieldGroupSelect
                label="In Rental"
                value={pendingInRental}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setPendingInRental(event.target.value)
                }
                optionNodes={yesNoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              />

              <OrganizationSystemSelect
                organization_id={organizationInfo?.id}
                onChange={(system: ISystem) => setPendingSystem(system)}
                selectedSystem={{
                  value: pendingSystem?.gid || '',
                  name: pendingSystem?.name || '',
                }}
                description="Select the system to filter the hardware"
              />
            </>
          }
          customKey={['id', 'type']}
          onRowCLick={(rowElement) => history.push(`/hardware/${rowElement.id}`)}
          sortBy={filters.sortConfig.key}
          sortDirection={filters.sortConfig.direction}
          onSortChange={(sort) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              sortConfig: sort as ISortConfig,
            }));
          }}
          columns={hardwareColumns as IColumn[]}
          items={hardwareItems}
          searchPlaceholder={`Search`}
          page={pageNumber}
          rowsPerPage={filters.sizePerPage}
          quickFilters={
            // Never render with an empty list: MultiActionChip's seeding effect
            // spins on it (see needsActiveStateSeed).
            quickTypeToggles.length > 0 ? (
              <MultiActionChip items={quickTypeToggles} />
            ) : undefined
          }
          noDataText={
            <>
              <Stack alignItems={'center'}>
                <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
                  <img
                    src={'/assets/hardware_banner.png'}
                    alt={''}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
                <Button
                  sx={{
                    bgcolor:
                      theme.palette.mode === 'light'
                        ? '#101C3D'
                        : theme.palette.secondary.main,
                    '&:hover': {
                      textDecoration: 'none',
                      color: '#FFFFFF',
                    },
                  }}
                  disableRipple
                  href={MOVATIC_SHOP}
                  target="_blank"
                  startIcon={<StoreMallDirectoryIcon />}
                  variant="contained"
                >
                  <Typography variant={'subtitle2'}>Build your fleet</Typography>
                </Button>
              </Stack>
            </>
          }
          onPageChange={(page, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(pageSizeString) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              sizePerPage: Number(pageSizeString.target.value),
            }));
          }}
          handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              search: e.target.value,
            }));
          }}
          searchText={filters.search}
        />
      </Card>
    </PageContainer>
  );
}

export default HardwareOrganizationPage;
